import React, { ReactElement, useCallback, useEffect, useRef } from 'react';

import {
    Button,
    CircleIcon,
    CalendarMonthIcon,
    ClickAwayListener,
    ColorRangeIcon,
    ColorRangeIconScale,
    DispersedSourceIcon,
    FilterIcon,
    Grid,
    MarkdownFormatter,
    PlumeIcon,
    Typography,
    useTheme,
    ZoomIcon,
    MapIcon
} from '@methanesat/ui-components';
import { BelowNavDrawer, BelowNavDrawerProps } from '../BelowNavDrawer';
import { useAppDispatch, useCurrentBreakpoint, useTranslate } from '../../hooks';
import { selectPlatform, toggleSingleChip } from '../../reducers';
import { tooltipSlice } from '../../reducers/tooltip';
import { getBreakpointsDown } from '../../utils/breakpoints';
import { getOgiColor } from '../EmissionsMapLegend/MiniLegend/OgiChip';
import { analytics, calculateDispersedIconColors, getColorRangeColors } from '../../utils';
import { METHANESAT_FAQ_URL, getTargetTotalsColors } from '../../consts';
import { AreaEmissionsProducts, Platforms } from '../../types';
import { useSelector } from 'react-redux';
import { ONE_MSAT_DATE_RANGE } from '../../environmentVariables';

type IntroDrawerProps = Omit<BelowNavDrawerProps, 'children'> & {
    onWidth: (width: number) => void;
    triggerRef?: React.RefObject<HTMLElement | null>;
    open: boolean;
};

/**
 * Full-height side drawer for map controls.
 * Under the app bar on larger screens,
 * truly full-height on mobile.
 */
const IntroDrawer = ({ onWidth, triggerRef, open, ...drawerProps }: IntroDrawerProps): ReactElement => {
    const t = useTranslate();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const drawerRef = useRef<HTMLDivElement>(null);
    const previousActiveElementRef = useRef<HTMLElement | null>(null);
    const headerRef = useRef<HTMLHeadingElement>(null);

    const drawerWidth = drawerRef.current?.getBoundingClientRect().width;
    const ogiInfrastructureColor = getOgiColor(theme, true);
    const breakpoint = useCurrentBreakpoint();

    const platform = useSelector(selectPlatform);

    const headerTriggerId = 'intro-drawer-header';

    // Get all focusable elements within the drawer
    const getFocusableElements = useCallback((): HTMLElement[] => {
        const drawer = drawerRef.current;
        if (!drawer) return [];

        const focusableSelectors = [
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'a[href]',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ');

        return Array.from(drawer.querySelectorAll(focusableSelectors)) as HTMLElement[];
    }, []);

    // Handle keyboard navigation for focus trapping
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!open) return;

            if (event.key === 'Tab') {
                const focusableElements = getFocusableElements();
                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (event.shiftKey) {
                    // Shift + Tab: move focus backwards
                    if (document.activeElement === firstElement) {
                        event.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    // Tab: move focus forwards
                    if (document.activeElement === lastElement) {
                        event.preventDefault();
                        firstElement.focus();
                    }
                }
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                drawerProps.onClose(event, 'escapeKeyDown');
            }
        },
        [open, drawerProps, getFocusableElements]
    );

    // Handle drawer width changes
    useEffect(() => {
        const down = getBreakpointsDown('drawer', theme);
        if (down.includes(breakpoint)) {
            onWidth(0);
        } else {
            onWidth(drawerWidth || 0);
        }
    }, [breakpoint, drawerWidth, onWidth, theme]);

    // Focus on close drawer button when drawer opens
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                const focusableElements = getFocusableElements();
                const closeButton = focusableElements.find(
                    (el: HTMLElement) => el.getAttribute('data-testid') === 'close-drawer-button'
                );
                if (closeButton) {
                    closeButton.focus();
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [open, getFocusableElements]);

    // Return focus to drawer triggering element when drawer closes
    useEffect(() => {
        if (open) {
            const currentActive = document.activeElement as HTMLElement;
            const triggerElement = triggerRef?.current;

            if (triggerElement) {
                previousActiveElementRef.current = triggerElement;
            } else if (currentActive && currentActive !== document.body && currentActive.tabIndex !== -1) {
                previousActiveElementRef.current = currentActive;
            } else {
                const helpButton = document.querySelector('#header-help-button');
                previousActiveElementRef.current = helpButton as HTMLElement;
            }

            if (triggerRef?.current) {
                triggerRef.current.setAttribute('aria-expanded', 'true');
            }

            document.addEventListener('keydown', handleKeyDown);

            return () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        } else {
            if (triggerRef?.current) {
                triggerRef.current.setAttribute('aria-expanded', 'false');
            }
            if (previousActiveElementRef.current) {
                const elementToFocus = previousActiveElementRef.current;
                setTimeout(() => {
                    if (elementToFocus && typeof elementToFocus.focus === 'function') {
                        elementToFocus.focus();
                    }
                }, 150);
            }
        }
    }, [open, handleKeyDown, triggerRef]);

    return (
        <BelowNavDrawer
            {...drawerProps}
            open={open}
            paperRef={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={headerTriggerId}
            aria-describedby="intro-drawer-description"
        >
            <Grid container spacing={1} paddingX={2} marginBottom={2}>
                {/** Header */}
                <Grid size={{ xs: 12 }}>
                    <Typography
                        variant="h3"
                        sx={{ letterSpacing: '-0.05rem' }}
                        ref={headerRef}
                        id={headerTriggerId}
                        component="h1"
                    >
                        {t('emissionsMapPage.introDrawer.introHeader')}
                    </Typography>
                </Grid>

                {/** Caption */}
                <Grid size={{ xs: 12 }} sx={{ marginBottom: theme.spacing(1), display: 'flex' }}>
                    <Typography variant="caption" id="intro-drawer-description">
                        {t('emissionsMapPage.introDrawer.description')}
                    </Typography>
                </Grid>

                {/** Explore the Map */}
                <Grid size={{ xs: 12 }} marginTop={1}>
                    <Typography variant="h5" component="h2">
                        {t('emissionsMapPage.introDrawer.explore')}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    {/* Data over time */}
                    {/* Hidden if global date picker is turned off for MSAT. Always visible for MAIR */}
                    {(!ONE_MSAT_DATE_RANGE || platform === Platforms.MAIR) && (
                        <ClickAwayListener
                            onClickAway={() => {
                                dispatch(tooltipSlice.actions.hide());
                            }}
                        >
                            <Button
                                data-testid="intro-drawer-data-over-time-button"
                                startIcon={<CalendarMonthIcon aria-hidden="true" />}
                                color="secondary"
                                fullWidth={true}
                                variant="contained"
                                aria-label={t('emissionsMapPage.introDrawer.accessibility.dataOverTimeButton')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    analytics.clickIntroZoom();
                                    dispatch(
                                        tooltipSlice.actions.show({
                                            targetSelector: '#date-option-1',
                                            text: t('emissionsMapPage.introDrawer.modals.changeDate')
                                        })
                                    );
                                }}
                            >
                                {t('emissionsMapPage.introDrawer.dataOverTime')}
                            </Button>
                        </ClickAwayListener>
                    )}

                    {/* base map buttons */}
                    <ClickAwayListener
                        onClickAway={() => {
                            dispatch(tooltipSlice.actions.hide());
                        }}
                    >
                        <Button
                            data-testid="intro-drawer-base-map-button"
                            startIcon={<MapIcon aria-hidden="true" />}
                            color="secondary"
                            fullWidth={true}
                            variant="contained"
                            aria-label={t('emissionsMapPage.introDrawer.accessibility.baseMapButton')}
                            onClick={(event) => {
                                event.stopPropagation();
                                analytics.clickIntroZoom();
                                dispatch(
                                    tooltipSlice.actions.show({
                                        targetSelector: '#base-map-button',
                                        text: t('emissionsMapPage.introDrawer.modals.baseMap')
                                    })
                                );
                            }}
                        >
                            {t('emissionsMapPage.introDrawer.baseMap')}
                        </Button>
                    </ClickAwayListener>

                    {/* zoom buttons */}
                    <ClickAwayListener
                        onClickAway={() => {
                            dispatch(tooltipSlice.actions.hide());
                        }}
                    >
                        <Button
                            data-testid="intro-drawer-zoom-button"
                            startIcon={<ZoomIcon aria-hidden="true" />}
                            color="secondary"
                            fullWidth={true}
                            variant="contained"
                            aria-label={t('emissionsMapPage.introDrawer.accessibility.zoomButton')}
                            onClick={(event) => {
                                event.stopPropagation();
                                analytics.clickIntroZoom();
                                dispatch(
                                    tooltipSlice.actions.show({
                                        targetSelector: '#map-zoom-in-button',
                                        text: t('emissionsMapPage.introDrawer.modals.zoomIn')
                                    })
                                );
                            }}
                        >
                            {t('emissionsMapPage.introDrawer.zoom')}
                        </Button>
                    </ClickAwayListener>
                </Grid>

                {/** Understanding data layers */}
                <Grid size={{ xs: 12 }} marginTop={1}>
                    <Typography variant="h5" component="h2">
                        {t('emissionsMapPage.introDrawer.understandingLayers')}
                    </Typography>
                </Grid>
                <Grid
                    size={{ xs: 12 }}
                    sx={{ marginBottom: theme.spacing(1), marginTop: theme.spacing(-1), display: 'flex' }}
                >
                    <Typography variant="caption">{t('emissionsMapPage.introDrawer.layerVisibility')}</Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    {/* regional emissions */}
                    <ClickAwayListener
                        onClickAway={() => {
                            dispatch(tooltipSlice.actions.hide());
                        }}
                    >
                        <Button
                            data-testid="intro-drawer-regional-emissions-button"
                            startIcon={
                                <ColorRangeIcon
                                    colors={getTargetTotalsColors(theme, true)}
                                    scale={ColorRangeIconScale.linear}
                                    aria-hidden="true"
                                />
                            }
                            color="secondary"
                            fullWidth={true}
                            variant="contained"
                            aria-label={t('emissionsMapPage.introDrawer.accessibility.regionalEmissionsButton')}
                            onClick={(event) => {
                                event.stopPropagation();
                                analytics.clickIntroRegionalEmissions();
                                dispatch(toggleSingleChip('isTargetEmissionsChipExpanded'));
                                dispatch(
                                    tooltipSlice.actions.show({
                                        targetSelector: '#target-emissions-expand',
                                        parentSelector: '[data-testid="target-emissions"]'
                                    })
                                );
                            }}
                        >
                            {t('emissionsMapPage.introDrawer.regionalEmissions')}
                        </Button>
                    </ClickAwayListener>
                    {/* area emissions */}
                    <ClickAwayListener
                        onClickAway={() => {
                            dispatch(tooltipSlice.actions.hide());
                        }}
                    >
                        <Button
                            data-testid="intro-drawer-area-emissions-button"
                            startIcon={
                                <DispersedSourceIcon
                                    colors={calculateDispersedIconColors(
                                        theme,
                                        true,
                                        AreaEmissionsProducts.l4,
                                        platform
                                    )}
                                    sx={{
                                        marginRight: '14px',
                                        marginLeft: '14px'
                                    }}
                                    aria-hidden="true"
                                />
                            }
                            color="secondary"
                            fullWidth={true}
                            variant="contained"
                            aria-label={t('emissionsMapPage.introDrawer.accessibility.areaEmissionsButton')}
                            onClick={(event) => {
                                event.stopPropagation();
                                analytics.clickIntroAreaEmissions();
                                dispatch(toggleSingleChip('isAreaEmissionsChipExpanded'));
                                dispatch(
                                    tooltipSlice.actions.show({
                                        targetSelector: '#area-emissions-expand',
                                        parentSelector: '[data-testid="area-emissions"]'
                                    })
                                );
                            }}
                        >
                            {t('emissionsMapPage.introDrawer.areaEmissions')}
                        </Button>
                    </ClickAwayListener>
                    {/* point sources */}
                    <ClickAwayListener
                        onClickAway={() => {
                            dispatch(tooltipSlice.actions.hide());
                        }}
                    >
                        <Button
                            data-testid="intro-drawer-point-source-button"
                            startIcon={
                                <PlumeIcon
                                    sx={{
                                        marginRight: '14px',
                                        marginLeft: '14px'
                                    }}
                                    colors={getColorRangeColors(theme, true)}
                                    aria-hidden="true"
                                />
                            }
                            color="secondary"
                            fullWidth={true}
                            variant="contained"
                            aria-label={t('emissionsMapPage.introDrawer.accessibility.pointSourceButton')}
                            onClick={(event) => {
                                event.stopPropagation();
                                analytics.clickIntroPointSources();
                                dispatch(toggleSingleChip('isPlumeEmissionsChipExpanded'));
                                dispatch(
                                    tooltipSlice.actions.show({
                                        targetSelector: '#plume-emissions-expand',
                                        parentSelector: '[data-testid="point-source-toggle-chip"]'
                                    })
                                );
                            }}
                        >
                            {t('emissionsMapPage.introDrawer.pointSource')}
                        </Button>
                    </ClickAwayListener>
                    {/* ogi/types */}
                    <ClickAwayListener
                        onClickAway={() => {
                            dispatch(tooltipSlice.actions.hide());
                        }}
                    >
                        <Button
                            data-testid="intro-drawer-reported-ogi-button"
                            startIcon={
                                <CircleIcon
                                    sx={{ color: ogiInfrastructureColor, stroke: 'grey', width: 48 }}
                                    aria-hidden="true"
                                />
                            }
                            color="secondary"
                            fullWidth={true}
                            variant="contained"
                            aria-label={t('emissionsMapPage.introDrawer.accessibility.ogiTypeFilterButton')}
                            onClick={(event) => {
                                event.stopPropagation();
                                analytics.clickIntroOgi();
                                dispatch(toggleSingleChip('isOilAndGasTypeChipExpanded'));
                                dispatch(
                                    tooltipSlice.actions.show({
                                        targetSelector: '#ogi-infrastructure-expand',
                                        parentSelector: '[data-testid="oil-and-gas-toggle-chip"]'
                                    })
                                );
                            }}
                        >
                            {t('emissionsMapPage.introDrawer.filterOGI')}
                        </Button>
                    </ClickAwayListener>
                    {/* filter OGI */}
                    <ClickAwayListener
                        onClickAway={() => {
                            dispatch(tooltipSlice.actions.hide());
                        }}
                    >
                        <Button
                            data-testid="intro-drawer-filter-ogi-button"
                            startIcon={<FilterIcon sx={{ width: 48 }} aria-hidden="true" />}
                            color="secondary"
                            fullWidth={true}
                            variant="contained"
                            aria-label={t('emissionsMapPage.introDrawer.accessibility.ogiFilterButton')}
                            onClick={(event) => {
                                event.stopPropagation();
                                analytics.clickIntroFilterOgi();
                                dispatch(toggleSingleChip('isOgiOperatorFilterExpanded'));
                                dispatch(
                                    tooltipSlice.actions.show({
                                        targetSelector: '#ogi-operator-filter-expand',
                                        parentSelector: '[data-testid="filter-ogi-chip"]'
                                    })
                                );
                            }}
                        >
                            {t('emissionsMapPage.introDrawer.filterOGIOperator')}
                        </Button>
                    </ClickAwayListener>
                </Grid>

                {/* Other questions */}
                <Grid size={{ xs: 12 }} marginTop={1}>
                    <Typography variant="h5" component="h2">
                        {t('emissionsMapPage.introDrawer.otherQuestions')}
                    </Typography>
                </Grid>

                {/** Info Links */}
                <Grid
                    container
                    sx={{
                        lineHeight: '0.5rem'
                    }}
                >
                    <Grid size={{ xs: 11 }} sx={{ fontSize: '0.75rem' }}>
                        <MarkdownFormatter
                            markdown={t('emissionsMapPage.introDrawer.forMoreInfo', {
                                link: METHANESAT_FAQ_URL
                            })}
                            components={{
                                a: ({ ...props }) => (
                                    <a
                                        {...props}
                                        style={{ color: 'inherit', fontWeight: 'bold', lineHeight: '1.45rem' }}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="intro-drawer-faq-link"
                                        aria-label={`${t('emissionsMapPage.introDrawer.accessibility.forMoreInfoLink', { link: '' })}`}
                                    />
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </BelowNavDrawer>
    );
};

export default IntroDrawer;
